import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { FUNCTIONS_QUERY_KEY } from 'constants/QueryKeys.constants';

import api from 'api';
import type { GetFunctionsResponse } from 'api/types';
import {
  PROJECT_RESOURCE,
  useProjectResource,
} from 'hooks/useProjectResources';
import useProjectRoute from 'hooks/useProjectRoute';
import useSampleProject from 'hooks/useSampleProjects/useSampleProject';
import { useRestApiEnabled } from 'hooks/useStreamSwitch';

const useFunctions = (
  enabled: boolean = true,
  overrideData?: GetFunctionsResponse | null, // pass null to override with empty data
) => {
  const { accountId, projectId } = useProjectRoute();
  const { isProjectResourceEnabled } = useProjectResource();
  const isEnabled =
    enabled &&
    (isProjectResourceEnabled(PROJECT_RESOURCE.FUNCTIONS) ||
      isProjectResourceEnabled(PROJECT_RESOURCE.KNOWLEDGE_BASE) ||
      isProjectResourceEnabled(PROJECT_RESOURCE.RULES));
  const { inSampleProject } = useSampleProject();
  const isRestApiEnabled = useRestApiEnabled();

  const queryFn =
    overrideData === undefined
      ? () => api.getFunctions(accountId, projectId)
      : () => overrideData || undefined;

  const {
    data,
    isLoading: isLoadingFunctions,
    isError: isLoadingFunctionsError,
    error: functionsError,
  } = useQuery({
    queryKey: [accountId, projectId, FUNCTIONS_QUERY_KEY],
    queryFn,
    enabled:
      !!(accountId && projectId) &&
      isEnabled &&
      !inSampleProject &&
      isRestApiEnabled,
  });

  return useMemo(
    () => ({
      startFunction: data?.active_functions?.find(
        f => f.function_type === 'conversation_start',
      ),
      endFunction: data?.active_functions?.find(
        f => f.function_type === 'conversation_end',
      ),
      functions: data?.functions ?? [],
      activeFunctions:
        data?.active_functions?.filter(f => !f.function_type) ?? [],
      flowFunctions:
        data?.active_functions?.filter(
          f => f.function_type === 'flow_function',
        ) ?? [],
      allActiveFunctions: data?.active_functions ?? [],
      isLoadingFunctions: isEnabled && isLoadingFunctions,
      isLoadingFunctionsError,
      functionsError,
    }),
    [
      isEnabled,
      data?.active_functions,
      data?.functions,
      isLoadingFunctions,
      isLoadingFunctionsError,
      functionsError,
    ],
  );
};

export default useFunctions;
